import { createPhoneSchema } from '@/components/FormFieldPhone';
import {
  HEARING_DATE_CASE_TYPE,
  FAX_REGEX,
  ZIP_CODE_SCHEMA,
  isValidCaseName,
  CASE_NAME_INVALID_MESSAGE,
  hasLetterOrDigit,
} from '@/utils/constant';
import { z } from 'zod';
import { PROPERTY_ADDRESS_CASE_TYPE } from '@/utils/constant';
import { Action, AfterAction, DepartmentType } from '@/types/workOrder.types';
import { RushType } from '@/types/case.types';

export enum WorkOrderServeTo {
  SERVER = 'SERVER',
  VENDOR = 'VENDOR',
}

const ATTORNEY_UUID_MESSAGE = 'Choose an attorney from the search results';

export const ClientDetailsSchema = z.object({
  contactId: z
    .string({ message: "Client's contact is required" })
    .min(1, { message: "Client's contact is required" })
    .uuid({ message: 'Choose a contact from the search results' }),
  type: z.enum(['PROCESS_SERVICE', 'DELIVERY', 'DUE_DILIGENCE_INQUIRY', 'HOME_INSPECTION'], {
    message: 'Service type is required',
  }),
  clientId: z.string({ message: 'Client # is required' }),
  clientName: z.string({ message: 'Client name is required' }),
  case: z.object({
    attorneyId: z
      .string({ message: "Attorney's name is required" })
      .min(1, { message: "Attorney's name is required" })
      .uuid({ message: ATTORNEY_UUID_MESSAGE }),
  }),
  process: z.object({
    represents: z.string({ message: 'Represents is required' }),
  }),
});

export const PeCaseDetailsSchema = z
  .object({
    case: z.object({
      jurisdiction: z.enum(['FEDERAL', 'STATE']).optional().nullable(),
      typeId: z.string().optional(),
      courtId: z.string().optional().nullable(),
      number: z.string().min(8, { message: 'Case # must have minimum 8 characters' }).optional().nullable(),
      address1: z.string().optional().nullable(),
      address2: z.string().optional().nullable(),
      city: z.string().optional().nullable(),
      state: z.string().min(2, { message: 'State is invalid' }).optional().nullable(),
      zipCode: ZIP_CODE_SCHEMA.optional().nullable(),
      name: z.string().refine(isValidCaseName, { message: CASE_NAME_INVALID_MESSAGE }).optional().nullable(),
      hasInsurance: z.boolean().optional().nullable(),
      plaintiff: z.string().optional().nullable(),
      defendant: z.string().optional().nullable(),
      serveBy: z.date().optional().nullable(),
      hearingOn: z.date().optional().nullable(),
      fileNo: z.string().optional().nullable(),
      insurance: z
        .object({
          fax: z.string().regex(FAX_REGEX, { message: 'Fax is invalid' }).optional().nullable(),
          dateOfLoss: z.date().optional().nullable(),
          billTo: z.string().optional().nullable(),
          policy: z
            .string()
            .regex(/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9/-]+$/, {
              message: 'Policy is invalid',
            })
            .optional()
            .nullable(),
          claim: z.string().optional().nullable(),
          adjuster: z.string().optional().nullable(),
          adjusterName: z.string().optional().nullable(),
          insuredName: z.string().optional().nullable(),
          phone: createPhoneSchema(false),
        })
        .optional(),
      rush: z.nativeEnum(RushType).nullish(),
    }),
    isPrepaid: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    if (!data.case.jurisdiction)
      ctx.addIssue({
        message: 'Jurisdiction is required',
        path: ['case.jurisdiction'],
        code: 'custom',
      });
    if (!data.case.typeId)
      ctx.addIssue({
        message: 'Case type is required',
        path: ['case.typeId'],
        code: 'custom',
      });
    if (data.case.typeId && PROPERTY_ADDRESS_CASE_TYPE.includes(data.case.typeId)) {
      if (!data.case.address1)
        ctx.addIssue({
          message: 'Property address line 1 is required',
          path: ['case.address1'],
          code: 'custom',
        });
      if (!data.case.city)
        ctx.addIssue({
          message: 'City is required',
          path: ['case.city'],
          code: 'custom',
        });
      if (!data.case.state)
        ctx.addIssue({
          message: 'State is required',
          path: ['case.state'],
          code: 'custom',
        });
      if (!data.case.zipCode)
        ctx.addIssue({
          message: 'Zip code is required',
          path: ['case.zipCode'],
          code: 'custom',
        });
    }
    if (!data.case.courtId)
      ctx.addIssue({
        message: 'Court name is required',
        path: ['case.courtId'],
        code: 'custom',
      });
    if (!data.case.number)
      ctx.addIssue({
        message: 'Case # is required',
        path: ['case.number'],
        code: 'custom',
      });
    if (!data.case.name)
      ctx.addIssue({
        message: 'Case name is required',
        path: ['case.name'],
        code: 'custom',
      });
    if (!data.case.fileNo)
      ctx.addIssue({
        message: 'File # is required',
        path: ['case.fileNo'],
        code: 'custom',
      });
    if (!data.case.serveBy)
      ctx.addIssue({
        message: 'Serve by is required',
        path: ['case.serveBy'],
        code: 'custom',
      });
    if (data.case.hasInsurance) {
      if (!data.case.insurance?.claim)
        ctx.addIssue({
          message: 'Claim # is required',
          path: ['case.insurance.claim'],
          code: 'custom',
        });
    }
    if (!data.case.plaintiff) {
      ctx.addIssue({
        message: 'Plaintiff is required',
        path: ['case.plaintiff'],
        code: 'custom',
      });
    }
    if (!data.case.defendant) {
      ctx.addIssue({
        message: 'Defendant is required',
        path: ['case.defendant'],
        code: 'custom',
      });
    }
    if (data.case.typeId === HEARING_DATE_CASE_TYPE.SUB || data.case.typeId === HEARING_DATE_CASE_TYPE.NY_LT)
      if (!data.case.hearingOn)
        ctx.addIssue({
          message: 'Hearing is required',
          path: ['case.hearingOn'],
          code: 'custom',
        });
  });

export const PrivateInfoSchema = z.object({
  ssn: z.string().min(11, 'Social security number is invalid').optional().nullish(),
  dateOfBirth: z.date().optional().nullish(),
  dlNumber: z.string().min(9, 'Driver license is invalid').optional().nullish(),
  others: z.string().optional().nullish(),
});

const WorkOrderPaymentSchema = z.object({
  issueDate: z.date().nullish(),
  reference: z.string().nullish(),
  issuedTo: z.string().nullish(),
  checkNumber: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '') return undefined;
      const num = Number(val);
      if (Number.isNaN(num)) return val;

      return num;
    },
    z
      .number({
        required_error: 'Check number is required',
        invalid_type_error: 'Check number must be a number',
      })
      .int({ message: 'Invalid check number' })
      .nonnegative({ message: 'Check number must be a positive number' })
      .max(Number.MAX_SAFE_INTEGER, { message: 'Invalid check number' })
      .nullish()
  ),
  amount: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '') return undefined;
      const num = Number(val);
      if (Number.isNaN(num)) return val;

      return num;
    },
    z
      .number({
        invalid_type_error: 'Amount must be a number',
      })
      .nonnegative({ message: 'Amount must be a positive number' })
      .max(Number.MAX_SAFE_INTEGER, { message: 'Invalid amount' })
      .nullish()
  ),
  status: z.string().nullish(),
  paymentId: z.string().nullish(),
});

export const ServerDetailsSchema = z
  .object({
    serverId: z.string().optional().nullish(),
    address1: z.string().optional().nullish(),
    address2: z.string().optional().nullish(),
    city: z.string().optional().nullish(),
    phone: createPhoneSchema(false),
    addressType: z.enum(['BUSINESS', 'HOME']).optional().nullish(),
    toServe: z.string().optional().nullish(),
    hasPrivateInfo: z.boolean().optional().nullish(),
    documents: z
      .record(z.boolean().optional())
      .refine((docs) => Object.values(docs).some((value) => value === true), {
        message: 'Description of documents are required',
      })
      .optional()
      .nullish(),
    // ZOHO-1467: caseDocTypeId → name edited for this work order only (unset = master name).
    documentNames: z.record(z.string().optional()).optional().nullish(),
    affidavitTypeId: z.string().optional().nullish(),
    serverInstructions: z.string().optional().nullish(),
    state: z.string().min(2, { message: 'State is invalid' }).optional().nullish(),
    zipCode: ZIP_CODE_SCHEMA.optional().nullish(),
    type: z.enum(['INDIVIDUAL', 'BUSINESS']).optional().nullish(),
    privateInfo: PrivateInfoSchema.optional().nullish(),
    serveTo: z.nativeEnum(WorkOrderServeTo).nullish(),
    vendorDetails: z
      .object({
        vendorId: z.string().nullish(),
        vendorContactId: z.string().nullish(),
        action: z.nativeEnum(Action).nullish(),
        department: z.nativeEnum(DepartmentType).nullish(),
        otherAction: z.string().nullish(),
        afterAction: z.nativeEnum(AfterAction).array().nullish(),
        otherAfterAction: z.string().nullish(),
        amount: z.preprocess(
          (val) => {
            if (val === null || val === undefined || val === '') return undefined;
            const num = Number(val);
            if (Number.isNaN(num)) return val;

            return num;
          },
          z
            .number({
              invalid_type_error: 'Amount must be a number',
            })
            .max(Number.MAX_SAFE_INTEGER, { message: 'Invalid amount' })
            .nullish()
        ),
        trackingNumber: z.string().nullish(),
      })
      .nullish(),
    hasWitnessFees: z.boolean().nullish(),
    payments: z.array(WorkOrderPaymentSchema).nullish(),
    witnessFee: WorkOrderPaymentSchema.nullish(),
    teamId: z.string().nullish(),
    userId: z.string().nullish(),
    includeHomeInspection: z.boolean().optional().default(false),
    includeHomeInspectionManualOverride: z.boolean().optional().default(false),
    sameAsPropertyAddress: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data.type) {
      ctx.addIssue({
        message: 'Type is required',
        path: ['type'],
        code: 'custom',
      });
    }
    if (!data.address1) {
      ctx.addIssue({
        message: 'Address line 1 is required',
        path: ['address1'],
        code: 'custom',
      });
    }
    if (!data.addressType) {
      ctx.addIssue({
        message: 'Address type is required',
        path: ['addressType'],
        code: 'custom',
      });
    }
    if (!data.city) {
      ctx.addIssue({
        message: 'City is required',
        path: ['city'],
        code: 'custom',
      });
    }
    if (!data.state) {
      ctx.addIssue({
        message: 'State is required',
        path: ['state'],
        code: 'custom',
      });
    }
    if (!data.zipCode) {
      ctx.addIssue({
        message: 'Zip code is required',
        path: ['zipCode'],
        code: 'custom',
      });
    }
    if (!data.documents) {
      ctx.addIssue({
        message: 'Documents are required',
        path: ['documents'],
        code: 'custom',
      });
    }
    if (!data.affidavitTypeId) {
      ctx.addIssue({
        message: 'Affidavit type is required',
        path: ['affidavitTypeId'],
        code: 'custom',
      });
    }
    if (data.type === 'BUSINESS' && !data.toServe) {
      ctx.addIssue({
        message: 'Business name is required',
        path: ['toServe'],
        code: 'custom',
      });
    }
    if (data.type === 'INDIVIDUAL' && !data.toServe) {
      ctx.addIssue({
        message: 'Person to serve is required',
        path: ['toServe'],
        code: 'custom',
      });
    }
    if (data.toServe && !hasLetterOrDigit(data.toServe)) {
      ctx.addIssue({
        message:
          data.type === 'BUSINESS'
            ? 'Business name must contain at least one letter or number'
            : 'Person to serve must contain at least one letter or number',
        path: ['toServe'],
        code: 'custom',
      });
    }
    if (data.hasPrivateInfo && data.type === 'INDIVIDUAL') {
      if (!data?.privateInfo?.dateOfBirth && !data?.privateInfo?.dlNumber && !data?.privateInfo?.ssn) {
        ctx.addIssue({
          message: 'At least one private information field is required',
          path: ['privateInfo'],
          code: 'custom',
        });
      }
    }

    if (data.serveTo === WorkOrderServeTo.VENDOR) {
      if (!data.vendorDetails?.vendorId)
        ctx.addIssue({
          message: 'Vendor name is required',
          path: ['vendorDetails', 'vendorId'],
          code: 'custom',
        });

      if (!data.vendorDetails?.amount)
        ctx.addIssue({
          message: 'Amount is required',
          path: ['vendorDetails', 'amount'],
          code: 'custom',
        });

      if (!data.vendorDetails?.department)
        ctx.addIssue({
          message: 'Department is required',
          path: ['vendorDetails', 'department'],
          code: 'custom',
        });

      if (!data.vendorDetails?.action)
        ctx.addIssue({
          message: 'Service action is required',
          path: ['vendorDetails', 'action'],
          code: 'custom',
        });

      if (data.vendorDetails?.action === Action.OTHER && !data.vendorDetails.otherAction)
        ctx.addIssue({
          message: 'Other service action is required',
          path: ['vendorDetails', 'otherAction'],
          code: 'custom',
        });

      if (!data.vendorDetails?.afterAction?.length)
        ctx.addIssue({
          message: 'After service action is required',
          path: ['vendorDetails', 'afterAction'],
          code: 'custom',
        });

      if (data.vendorDetails?.afterAction?.includes(AfterAction.OTHER) && !data.vendorDetails.otherAfterAction)
        ctx.addIssue({
          message: 'Other after service action is required',
          path: ['vendorDetails', 'otherAfterAction'],
          code: 'custom',
        });
    }

    if (data.hasWitnessFees) {
      if (!data.witnessFee?.amount)
        ctx.addIssue({
          message: 'Amount is required',
          path: ['witnessFee', 'amount'],
          code: 'custom',
        });
      if (!data.witnessFee?.checkNumber)
        ctx.addIssue({
          message: 'Check number is required',
          path: ['witnessFee', 'checkNumber'],
          code: 'custom',
        });
      if (!data.witnessFee?.issuedTo)
        ctx.addIssue({
          message: 'Issued to is required',
          path: ['witnessFee', 'issuedTo'],
          code: 'custom',
        });
      if (!data.witnessFee?.issueDate)
        ctx.addIssue({
          message: 'Issue date is required',
          path: ['witnessFee', 'issueDate'],
          code: 'custom',
        });
    }

    if (data.payments?.length) {
      data.payments.forEach((payment, index) => {
        if (!payment.amount)
          ctx.addIssue({
            message: 'Amount is required',
            path: ['payments', index, 'amount'],
            code: 'custom',
          });
        if (!payment.issuedTo)
          ctx.addIssue({
            message: 'Issued to is required',
            path: ['payments', index, 'issuedTo'],
            code: 'custom',
          });
        if (!payment.issueDate)
          ctx.addIssue({
            message: 'Issue date is required',
            path: ['payments', index, 'issueDate'],
            code: 'custom',
          });
      });
    }
  });

export const PeWoDetailsSchema = z.object({
  workOrders: z.array(ServerDetailsSchema),
});

/** Case types that auto-check Include Home Inspection (issue #420).
 * IDs match dgr-api `seedCaseTypes` / live `caseTypeGetAll` (`PI`, `VAI`). */
export const HI_AUTO_INCLUDE_CASE_TYPES = ['PI', 'VAI'] as const;

export const shouldAutoIncludeHomeInspection = (caseTypeId: string | null | undefined): boolean =>
  !!caseTypeId && (HI_AUTO_INCLUDE_CASE_TYPES as readonly string[]).includes(caseTypeId);

/** Work order form fields that only drive the UI and must not be sent to the API. */
const FORM_ONLY_WORK_ORDER_FIELDS = [
  'includeHomeInspection',
  'includeHomeInspectionManualOverride',
  'sameAsPropertyAddress',
  'documentNames',
] as const;

export const omitFormOnlyWorkOrderFields = <T extends object>(
  workOrder: T
): Omit<T, (typeof FORM_ONLY_WORK_ORDER_FIELDS)[number]> => {
  const rest: Record<string, unknown> = { ...workOrder };
  FORM_ONLY_WORK_ORDER_FIELDS.forEach((field) => delete rest[field]);
  return rest as Omit<T, (typeof FORM_ONLY_WORK_ORDER_FIELDS)[number]>;
};

/** Foreclosure case types whose Property Address State is fixed (DGR-1821). */
export const PROPERTY_STATE_BY_CASE_TYPE: ReadonlyMap<string, string> = new Map([
  ['NY_FC', 'NY'],
  ['PA_FC', 'PA'],
]);

export const getPropertyStateForCaseType = (caseTypeId: string | null | undefined): string | undefined =>
  caseTypeId ? PROPERTY_STATE_BY_CASE_TYPE.get(caseTypeId) : undefined;
